import React, { useState } from 'react';
import { BookOpen, Layers, Calendar, Volume2, Shield, TrendingUp, Users, Umbrella, RefreshCw, Lock, Eye, ChevronRight, Download } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { bookSection } from '../data/mock';
import { toast } from '../hooks/use-toast';

const iconMap = {
  Layers: Layers,
  Calendar: Calendar,
  Volume2: Volume2,
  Shield: Shield,
  TrendingUp: TrendingUp,
  Users: Users,
  Umbrella: Umbrella,
  RefreshCw: RefreshCw,
  Lock: Lock,
  Eye: Eye
};

const BookSection = () => {
  const { language } = useLanguage();
  const [selectedBrick, setSelectedBrick] = useState(null);

  const handlePreOrder = () => {
    toast({
      title: language === 'en' ? 'Pre-order Coming Soon!' : '¡Pre-orden Próximamente!',
      description: language === 'en' 
        ? 'The Endler Wall book will be available for pre-order soon.' 
        : 'El libro El Muro Endler estará disponible para pre-ordenar pronto.',
    });
  };

  const handleReadSample = () => {
    toast({
      title: language === 'en' ? 'Sample Chapter' : 'Capítulo de Muestra',
      description: language === 'en' 
        ? 'Check your email for the first chapter of The Endler Wall!' 
        : '¡Revisa tu email para el primer capítulo de El Muro Endler!',
    });
  };

  return (
    <section className="py-24 bg-gradient-to-b from-[#0d1f1f] to-[#1a3334] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        {/* Badge */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-6 py-2 backdrop-blur-sm">
            <BookOpen className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300 text-sm font-medium">
              {language === 'en' ? bookSection.badge_en : bookSection.badge_es}
            </span>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-5xl md:text-6xl font-bold text-white">
            {language === 'en' ? bookSection.title_en : bookSection.title_es}
          </h2>
          <p className="text-xl md:text-2xl text-emerald-400 font-medium">
            {language === 'en' ? bookSection.subtitle_en : bookSection.subtitle_es}
          </p>
        </div>

        {/* Book Cover & Description */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          {/* Book Cover */}
          <div className="relative">
            <div className="aspect-[3/4] max-w-md mx-auto bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl shadow-2xl shadow-emerald-500/20 border-4 border-emerald-500/20 overflow-hidden relative group hover:scale-105 transition-transform duration-500">
              {/* Book Cover Design */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-700 to-[#0d1f1f] p-12 flex flex-col justify-between">
                {/* Title on cover */}
                <div className="space-y-4">
                  <div className="text-white text-4xl font-bold leading-tight">
                    EL MURO<br />ENDLER
                  </div>
                  <div className="text-emerald-200 text-lg">
                    Cómo construir una defensa<br />inquebrantable y conquistar<br />el mundo
                  </div>
                </div>

                {/* Center image placeholder */}
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-48 h-48 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border-4 border-white/20">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_endler-stats/artifacts/4is68jqw_IMG_0018.png" 
                      alt="Christiane Endler" 
                      className="w-full h-full object-cover rounded-full opacity-80"
                    />
                  </div>
                </div>

                {/* Author */}
                <div className="text-white text-lg font-semibold">
                  Christiane Endler
                </div>
              </div>

              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/0 via-emerald-500/0 to-emerald-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>

            {/* Floating badges */}
            <div className="absolute -top-6 -right-6 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl p-4 shadow-2xl transform rotate-6 hover:rotate-0 transition-transform duration-300">
              <div className="text-white text-center">
                <div className="text-2xl font-bold">8</div>
                <div className="text-xs">Capítulos</div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-8">
            <div className="space-y-4">
              <p className="text-lg text-gray-300 leading-relaxed">
                {language === 'en' ? bookSection.description_en : bookSection.description_es}
              </p>
              
              <blockquote className="relative pl-6 border-l-4 border-emerald-500">
                <p className="text-xl text-emerald-100 italic">
                  {language === 'en' ? bookSection.quote_en : bookSection.quote_es}
                </p>
              </blockquote>
            </div>

            {/* Chapters Preview */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50">
              <h3 className="text-white font-semibold text-xl mb-4">
                {language === 'en' ? 'Chapters' : 'Capítulos'}
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                {bookSection.chapters.map((chapter) => (
                  <div 
                    key={chapter.number}
                    className="flex items-start space-x-3 text-gray-300 hover:text-emerald-400 transition-colors cursor-pointer p-2 rounded-lg hover:bg-gray-700/30"
                  >
                    <span className="text-emerald-400 font-bold text-sm">{chapter.number}.</span>
                    <div className="flex-1">
                      <div className="font-semibold text-sm">
                        {language === 'en' ? chapter.title_en : chapter.title_es}
                      </div>
                      <div className="text-xs text-gray-500">
                        {language === 'en' ? chapter.subtitle_en : chapter.subtitle_es}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={handlePreOrder}
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold group"
              >
                {language === 'en' ? bookSection.cta_primary_en : bookSection.cta_primary_es}
                <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleReadSample}
                className="border-2 border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-[#0d1f1f] font-semibold"
              >
                <Download className="mr-2" />
                {language === 'en' ? bookSection.cta_secondary_en : bookSection.cta_secondary_es}
              </Button>
            </div>
          </div>
        </div>

        {/* The 10 Bricks */}
        <div className="space-y-12">
          <div className="text-center space-y-4">
            <h3 className="text-4xl md:text-5xl font-bold text-white">
              {language === 'en' ? 'The 10 Bricks' : 'Los 10 Ladrillos'}
            </h3>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {language === 'en' 
                ? 'The manual to build your own unbreakable wall' 
                : 'El manual para construir tu propio muro inquebrantable'}
            </p>
          </div>

          {/* Bricks Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {bookSection.bricks.map((brick) => {
              const Icon = iconMap[brick.icon];
              const isSelected = selectedBrick === brick.number;
              
              return (
                <div
                  key={brick.number}
                  onClick={() => setSelectedBrick(isSelected ? null : brick.number)}
                  className={`
                    relative group cursor-pointer transition-all duration-300
                    ${isSelected ? 'lg:col-span-2 lg:row-span-2' : ''}
                  `}
                >
                  <div className={`
                    bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm 
                    rounded-2xl p-6 border-2 
                    ${isSelected 
                      ? 'border-emerald-500 shadow-2xl shadow-emerald-500/30' 
                      : 'border-gray-700/50 hover:border-emerald-500/50'
                    }
                    transition-all duration-300 h-full
                    ${isSelected ? '' : 'hover:scale-105'}
                  `}>
                    {/* Brick Number */}
                    <div className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center font-bold text-white shadow-lg">
                      {brick.number}
                    </div>

                    {/* Icon */}
                    <div className={`
                      inline-flex items-center justify-center rounded-xl bg-emerald-500/20 border border-emerald-500/30 mb-4
                      ${isSelected ? 'w-16 h-16' : 'w-12 h-12'}
                      transition-all duration-300
                    `}>
                      <Icon className={`
                        text-emerald-400
                        ${isSelected ? 'w-8 h-8' : 'w-6 h-6'}
                      `} />
                    </div>

                    {/* Content */}
                    <div className="space-y-2">
                      <h4 className={`
                        font-bold text-white
                        ${isSelected ? 'text-2xl' : 'text-lg'}
                        transition-all duration-300
                      `}>
                        {language === 'en' ? brick.title_en : brick.title_es}
                      </h4>
                      
                      {isSelected && (
                        <div className="space-y-3 animate-fadeIn">
                          <p className="text-emerald-400 font-medium">
                            {language === 'en' ? brick.subtitle_en : brick.subtitle_es}
                          </p>
                          <p className="text-gray-300 text-sm leading-relaxed">
                            {language === 'en' ? brick.description_en : brick.description_es}
                          </p>
                        </div>
                      )}
                      
                      {!isSelected && (
                        <p className="text-gray-400 text-sm line-clamp-2">
                          {language === 'en' ? brick.subtitle_en : brick.subtitle_es}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Helper text */}
          <div className="text-center">
            <p className="text-gray-500 text-sm">
              {language === 'en' 
                ? 'Click on any brick to learn more' 
                : 'Haz clic en cualquier ladrillo para aprender más'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookSection;
